import { forwardRef, useId, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import ImageComponent from '../../image';
import TextPrompt from '../text-prompt';

type InputStatus = 'default' | 'success' | 'warning' | 'error';

interface IInput extends React.ComponentPropsWithoutRef<'input'> {
	label?: string;
	error?: string | undefined;
	isonlyview?: boolean;
	minComponent?: boolean;
	imageUrl?: string;
	// New enhanced props
	helperText?: string;
	successMessage?: string;
	warningMessage?: string;
	status?: InputStatus;
	showCharacterCount?: boolean;
	maxCharacters?: number;
	leftIcon?: React.ReactNode;
	rightIcon?: React.ReactNode;
	isRequired?: boolean;
	hideStatusIcon?: boolean;
}

// Success checkmark animation
const checkmarkAnimation = keyframes`
	0% {
		stroke-dashoffset: 20;
		opacity: 0;
	}
	100% {
		stroke-dashoffset: 0;
		opacity: 1;
	}
`;

// Shake animation for errors
const shakeAnimation = keyframes`
	0%, 100% { transform: translateX(0); }
	10%, 30%, 50%, 70%, 90% { transform: translateX(-3px); }
	20%, 40%, 60%, 80% { transform: translateX(3px); }
`;

const SuccessIcon = () => (
	<svg
		width='16'
		height='16'
		viewBox='0 0 16 16'
		fill='none'
		xmlns='http://www.w3.org/2000/svg'
		className='status-icon success-icon'
	>
		<circle
			cx='8'
			cy='8'
			r='7'
			stroke='var(--color-success, #4caf50)'
			strokeWidth='1.5'
			fill='var(--color-success, #4caf50)'
			fillOpacity='0.1'
		/>
		<path
			d='M5 8L7 10L11 6'
			stroke='var(--color-success, #4caf50)'
			strokeWidth='1.5'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</svg>
);

const WarningIcon = () => (
	<svg
		width='16'
		height='16'
		viewBox='0 0 16 16'
		fill='none'
		xmlns='http://www.w3.org/2000/svg'
		className='status-icon warning-icon'
	>
		<path
			d='M8 2L14.5 13H1.5L8 2Z'
			stroke='var(--color-warning, #ffc107)'
			strokeWidth='1.5'
			fill='var(--color-warning, #ffc107)'
			fillOpacity='0.1'
			strokeLinejoin='round'
		/>
		<path
			d='M8 6V9'
			stroke='var(--color-warning, #ffc107)'
			strokeWidth='1.5'
			strokeLinecap='round'
		/>
		<circle
			cx='8'
			cy='11'
			r='0.75'
			fill='var(--color-warning, #ffc107)'
		/>
	</svg>
);

const ErrorIcon = () => (
	<svg
		width='16'
		height='16'
		viewBox='0 0 16 16'
		fill='none'
		xmlns='http://www.w3.org/2000/svg'
		className='status-icon error-icon'
	>
		<circle
			cx='8'
			cy='8'
			r='7'
			stroke='var(--color-danger, #f56e9d)'
			strokeWidth='1.5'
			fill='var(--color-danger, #f56e9d)'
			fillOpacity='0.1'
		/>
		<path
			d='M6 6L10 10M10 6L6 10'
			stroke='var(--color-danger, #f56e9d)'
			strokeWidth='1.5'
			strokeLinecap='round'
		/>
	</svg>
);

const getStatusFromProps = (
	error?: string,
	successMessage?: string,
	warningMessage?: string,
	status?: InputStatus
): InputStatus => {
	if (status) return status;
	if (error) return 'error';
	if (successMessage) return 'success';
	if (warningMessage) return 'warning';
	return 'default';
};

export const TypeInput = forwardRef<HTMLInputElement, IInput>(
	(
		{
			label,
			error,
			isonlyview,
			minComponent,
			imageUrl,
			helperText,
			successMessage,
			warningMessage,
			status: statusProp,
			showCharacterCount,
			maxCharacters,
			leftIcon,
			rightIcon,
			isRequired,
			hideStatusIcon,
			onChange,
			...props
		},
		ref
	) => {
		const [charCount, setCharCount] = useState(0);
		const uniqueId = useId();
		const inputId = props.id || props.name || uniqueId;
		const status = getStatusFromProps(error, successMessage, warningMessage, statusProp);
		const hasError = status === 'error';
		const hasSuccess = status === 'success';
		const hasWarning = status === 'warning';

		const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			if (showCharacterCount || maxCharacters) {
				setCharCount(e.target.value.length);
			}
			onChange?.(e);
		};

		const getMessage = () => {
			if (error) return error;
			if (successMessage) return successMessage;
			if (warningMessage) return warningMessage;
			return helperText;
		};

		const renderStatusIcon = () => {
			if (hideStatusIcon || isonlyview) return null;
			if (hasSuccess) return <SuccessIcon />;
			if (hasWarning) return <WarningIcon />;
			if (hasError) return <ErrorIcon />;
			return rightIcon || null;
		};

		const isOverLimit = maxCharacters && charCount > maxCharacters;

		return (
			<InputComponent
				className={props.className || ''}
				$status={status}
				$hasError={hasError}
				$hasSuccess={hasSuccess}
				$hasWarning={hasWarning}
			>
				<div className={`form-container f-column-9 position-relative ${props.className || ''}`}>
					{(label || imageUrl) && (
						<div className='label-row f-row-17 align-items-center'>
							{imageUrl && (
								<ImageComponent
									src={imageUrl}
									alt='logo'
									style={{
										width: '25px',
										height: '25px',
										objectFit: 'contain',
									}}
								/>
							)}
							{label && (
								<label
									htmlFor={inputId}
									className='m-0'
								>
									{label}
									{isRequired && (
										<span
											className='required-indicator'
											aria-hidden='true'
										>
											*
										</span>
									)}
								</label>
							)}
						</div>
					)}
					<div className='input-wrapper w-100'>
						{leftIcon && <span className='input-icon left-icon'>{leftIcon}</span>}
						<input
							ref={ref}
							id={inputId}
							disabled={props.disabled || isonlyview}
							{...props}
							onChange={handleChange}
							className={`${hasError ? 'is-error' : ''} ${hasSuccess ? 'is-success' : ''} ${hasWarning ? 'is-warning' : ''} ${isonlyview ? 'isonlyview' : ''} ${minComponent ? 'min-size' : ''} ${leftIcon ? 'has-left-icon' : ''} ${rightIcon || !hideStatusIcon ? 'has-right-icon' : ''}`}
							lang='en-GB'
							aria-invalid={hasError}
							aria-describedby={getMessage() ? `${inputId}-message` : undefined}
							maxLength={maxCharacters}
						/>
						{renderStatusIcon() && <span className='input-icon right-icon'>{renderStatusIcon()}</span>}
					</div>
					{(getMessage() || maxCharacters) && (
						<div className='input-footer'>
							<TextPrompt
								prompt={getMessage()}
								status={hasSuccess}
								noStatus={!hasError && !hasSuccess}
							/>
							{(showCharacterCount || maxCharacters) && (
								<span className={`character-count ${isOverLimit ? 'over-limit' : ''}`}>
									{charCount}
									{maxCharacters ? `/${maxCharacters}` : ''}
								</span>
							)}
						</div>
					)}
				</div>
			</InputComponent>
		);
	}
);

TypeInput.displayName = 'TypeInput';

const InputComponent = styled.div<{
	$status?: InputStatus;
	$hasError?: boolean;
	$hasSuccess?: boolean;
	$hasWarning?: boolean;
}>`
	.label-row {
		margin-bottom: var(--spacing-2, 0.5rem);

		label {
			color: var(--text-label, #5a5a5a);
			font-size: var(--font-size-base, 0.875rem);
			font-weight: 500;
			transition: color var(--transition-fast, 100ms) ease;
		}

		.required-indicator {
			color: var(--color-danger, #f56e9d);
			margin-left: 2px;
		}
	}

	.input-wrapper {
		position: relative;
		display: flex;
		align-items: center;

		.input-icon {
			position: absolute;
			display: flex;
			align-items: center;
			justify-content: center;
			pointer-events: none;
			color: var(--text-tertiary, #757575);
			transition: color var(--transition-fast, 100ms) ease;
			z-index: 1;

			&.left-icon {
				left: 0.75rem;
			}

			&.right-icon {
				right: 0.75rem;
			}

			.status-icon {
				animation: ${checkmarkAnimation} var(--transition-normal, 200ms) ease-out forwards;
			}
		}
	}

	.input-footer {
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		margin-top: var(--spacing-1, 0.25rem);
		min-height: 1.25rem;

		.character-count {
			font-size: var(--font-size-xs, 0.675rem);
			color: var(--text-muted, #9e9e9e);
			margin-left: auto;
			transition: color var(--transition-fast, 100ms) ease;

			&.over-limit {
				color: var(--color-danger, #f56e9d);
				font-weight: 500;
			}
		}
	}

	input {
		width: 100%;
		height: 3.125rem;
		background: var(--input-bg, #ffffff);
		border: 1.5px solid var(--input-border, #a1a1a1);
		border-radius: var(--radius-sm, 0.1875rem);
		padding: 0 1rem 0 0.75rem;
		box-shadow: none;
		box-sizing: border-box;
		outline: none;
		font-size: var(--font-size-base, 0.875rem);
		color: var(--text-primary, #212121);
		transition:
			border-color var(--transition-fast, 100ms) ease,
			box-shadow var(--transition-fast, 100ms) ease,
			background-color var(--transition-fast, 100ms) ease;

		&.has-left-icon {
			padding-left: 2.5rem;
		}

		&.has-right-icon {
			padding-right: 2.5rem;
		}

		&::placeholder {
			color: var(--input-placeholder, #748181);
			font-size: 0.8125rem;
			font-style: normal;
			font-weight: 300;
			line-height: normal;
		}

		&:-webkit-autofill {
			-webkit-box-shadow: 0 0 0 100rem var(--input-bg, #ffffff) inset;
			-webkit-text-fill-color: var(--text-primary, black);
		}

		&:-webkit-autofill:hover,
		&:-webkit-autofill:focus,
		&:-webkit-autofill:active {
			-webkit-box-shadow: 0 0 0 100rem var(--input-bg, #ffffff) inset;
			-webkit-text-fill-color: var(--text-primary, black);
		}

		&:hover:not(:disabled):not(.isonlyview) {
			border-color: var(--input-border-hover, #757575);
		}

		&:focus {
			border-color: var(--input-border-focus, #1b6b4a);
			box-shadow: var(--shadow-focus, 0 0 0 0.25rem rgba(27, 107, 74, 0.25));
		}

		&:active {
			border-color: var(--input-border-focus, #1b6b4a);
		}

		&:disabled {
			background: var(--bg-tertiary, #f5f5f5);
			color: var(--text-muted, #9e9e9e);
			cursor: not-allowed;
			border-color: var(--border-secondary, #e0e0e0);
		}

		/* Success state */
		&.is-success {
			border-color: var(--input-success-border, #4caf50);
			background: rgba(76, 175, 80, 0.03);

			&:focus {
				box-shadow: var(--shadow-focus-success, 0 0 0 0.25rem rgba(76, 175, 80, 0.25));
			}
		}

		/* Warning state */
		&.is-warning {
			border-color: var(--input-warning-border, #ffc107);
			background: rgba(255, 193, 7, 0.03);

			&:focus {
				box-shadow: 0 0 0 0.25rem rgba(255, 193, 7, 0.25);
			}
		}

		/* Error state */
		&.is-error {
			border-color: var(--border-error, #f56e9d);
			background: var(--input-error-bg, #fffbfb);
			animation: ${shakeAnimation} 0.4s ease;

			&:focus {
				box-shadow: var(--shadow-focus-error, 0 0 0 0.25rem rgba(245, 110, 157, 0.25));
			}
		}

		&.isonlyview {
			padding-left: 0;
			border: none;
			background: transparent;

			&:focus {
				box-shadow: none;
			}
		}

		&.min-size {
			font-size: 12px;
			height: 40px;
			border: 1px solid var(--border-primary, #d1d1d1);

			@media (max-width: 768px) {
				font-size: 16px;
			}
		}
	}

	/* Focus state label color change */
	&:focus-within {
		.label-row label {
			color: var(--color-primary, #1b6b4a);
		}
	}

	/* Status-specific label colors */
	${({ $hasError }) =>
		$hasError &&
		css`
			.label-row label {
				color: var(--color-danger, #f56e9d);
			}
		`}

	${({ $hasSuccess }) =>
		$hasSuccess &&
		css`
			.label-row label {
				color: var(--color-success, #4caf50);
			}
		`}

	${({ $hasWarning }) =>
		$hasWarning &&
		css`
			.label-row label {
				color: var(--color-warning, #d4a300);
			}
		`}
`;
